import React, { useRef, useEffect } from 'react';
import './RichTextEditor.css';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange }) => {
  const editorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const execCommand = (command: string, value: string | undefined = undefined) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    handleInput();
  };

  const insertList = (type: 'insertUnorderedList' | 'insertOrderedList') => {
    execCommand(type);
  };

  return (
    <div className="rich-text-editor">
      <div className="editor-toolbar">
        <div className="toolbar-group">
          <button
            type="button"
            onClick={() => execCommand('bold')}
            className="toolbar-btn"
            title="Kalın (Ctrl+B)"
          >
            <strong>B</strong>
          </button>
          <button
            type="button"
            onClick={() => execCommand('italic')}
            className="toolbar-btn"
            title="İtalik (Ctrl+I)"
          >
            <em>I</em>
          </button>
          <button
            type="button"
            onClick={() => execCommand('underline')}
            className="toolbar-btn"
            title="Altı Çizili (Ctrl+U)"
          >
            <u>U</u>
          </button>
          <button
            type="button"
            onClick={() => execCommand('strikeThrough')}
            className="toolbar-btn"
            title="Üstü Çizili"
          >
            <s>S</s>
          </button>
        </div>

        <div className="toolbar-divider"></div>

        <div className="toolbar-group">
          <select
            onChange={(e) => execCommand('formatBlock', e.target.value)}
            className="toolbar-select"
            defaultValue=""
          >
            <option value="">Normal</option>
            <option value="h1">Başlık 1</option>
            <option value="h2">Başlık 2</option>
            <option value="h3">Başlık 3</option>
          </select>
        </div>

        <div className="toolbar-divider"></div>

        <div className="toolbar-group">
          <button
            type="button"
            onClick={() => insertList('insertUnorderedList')}
            className="toolbar-btn"
            title="Madde İşaretleri"
          >
            • Liste
          </button>
          <button
            type="button"
            onClick={() => insertList('insertOrderedList')}
            className="toolbar-btn"
            title="Numaralı Liste"
          >
            1. Liste
          </button>
        </div>

        <div className="toolbar-divider"></div>

        <div className="toolbar-group">
          <label className="color-picker-label">
            <span>Metin</span>
            <input
              type="color"
              onChange={(e) => execCommand('foreColor', e.target.value)}
              className="color-picker"
            />
          </label>
          <label className="color-picker-label">
            <span>Arka Plan</span>
            <input
              type="color"
              onChange={(e) => execCommand('backColor', e.target.value)}
              className="color-picker"
            />
          </label>
        </div>

        <div className="toolbar-divider"></div>

        <div className="toolbar-group">
          <button
            type="button"
            onClick={() => execCommand('justifyLeft')}
            className="toolbar-btn"
            title="Sola Hizala"
          >
            ⬅
          </button>
          <button
            type="button"
            onClick={() => execCommand('justifyCenter')}
            className="toolbar-btn"
            title="Ortala"
          >
            ⬌
          </button>
          <button
            type="button"
            onClick={() => execCommand('justifyRight')}
            className="toolbar-btn"
            title="Sağa Hizala"
          >
            ➡
          </button>
        </div>

        <div className="toolbar-divider"></div>

        <div className="toolbar-group">
          <button
            type="button"
            onClick={() => execCommand('removeFormat')}
            className="toolbar-btn"
            title="Formatı Temizle"
          >
            🧹
          </button>
        </div>
      </div>

      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        className="editor-content"
        suppressContentEditableWarning
      />
    </div>
  );
};

export default RichTextEditor;
